import { css, ThemeContext } from '@emotion/react';
import styled from '@emotion/styled';
import { useContext, useState, useEffect, useMemo } from 'react';
import { useUID } from 'react-uid';
import { useTranslation } from 'react-i18next';

import { Modal, Flex, Heading, BorderlessButton, Button } from 'components';

import {
  GameTypeEnum,
  GameDifficulty,
  SceneStatusEnum,
  MeWithTutorialQuery_me_company_gamesConfig,
} from 'graphql/types';

import { AppTheme } from 'theme';

import colors from 'lib/colors';

import { SceneWithProgress } from 'pages/Lobby/ScenesProgressProvider';

import { OnlineUsersContext } from '../LobbyGame/OnlineUsersProvider';
import { StartTrainingContext } from '../LobbyGame/StartTrainingProvider';

import SceneDetailModalTabPanel from './SceneDetailModalTabPanel';
import { NavigationLinkContent } from './LobbyDifficulty';
import { SceneContent } from './MatchView/SceneList/Scene';
import ScenesProgressHeading from './ScenesProgressHeading';

interface SceneDetailModalProps {
  isOpen: boolean;
  isPlayButtonDisabled: boolean;
  scene: SceneWithProgress;
  gameDifficulty: GameDifficulty;
  gameType: GameTypeEnum;
  tutorialSceneName?: string;
  gamesConfig?: MeWithTutorialQuery_me_company_gamesConfig[];
  onClose?: () => void;
}

const SceneDetailModal = ({
  isOpen,
  isPlayButtonDisabled,
  onClose,
  gameDifficulty,
  gameType,
  gamesConfig,
  scene,
  tutorialSceneName,
}: SceneDetailModalProps) => {
  const [t] = useTranslation();
  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;
  const LOCIZE_SUFFIX_KEY = IS_EDU ? '.edu' : '';

  const IS_EASY_GAME = gameDifficulty === GameDifficulty.EASY;

  const { startingTutorial, creatingTutorial, handleStartTutorial } =
    useContext(StartTrainingContext);

  const IS_BUTTON_DISABLED = startingTutorial || creatingTutorial;
  const isTraining =
    (scene?.statuses || []).indexOf(SceneStatusEnum.TRAINING) > -1;

  const { opponentCount, clearSearch } = useContext(OnlineUsersContext);

  // To minimalize re-rendering in useEffect below
  const gameMenuList = useMemo(
    () =>
      [
        {
          key: 'tournament',
          gameType: GameTypeEnum.TOURNAMENT_GAME,
          isHidden:
            (scene?.statuses || []).indexOf(SceneStatusEnum.TOURNAMENT) === -1,
        },
        {
          key: 'training',
          gameType: GameTypeEnum.TRAINING_GAME,
          isHidden: !isTraining,
        },
        {
          key: 'play',
          gameType: GameTypeEnum.FREE_GAME,
          isHidden: false,
        },
        {
          key: 'unfinishedGames',
          isHidden:
            (scene?.statuses || []).indexOf(SceneStatusEnum.UNFINISHED) === -1,
        },
      ].filter((g) => !g.isHidden),
    [isTraining, scene],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedTabKey(gameMenuList[0].key);
    }
  }, [isOpen, gameMenuList]);

  const [selectedTabKey, setSelectedTabKey] = useState(gameMenuList[0].key);

  const modalHeadingId = useUID();

  const handleClose = () => {
    onClose && onClose();
    clearSearch && clearSearch();
  };

  const handleTabSelect = (tabKey: string) => {
    setSelectedTabKey(tabKey);
    clearSearch && clearSearch();
  };

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      showClose
      onRequestClose={handleClose}
      aria={{
        labelledby: modalHeadingId,
      }}
      css={css`
        .Overlay {
          z-index: 10;
        }
        .Modal {
          background-color: ${theme.colors.black};
          color: ${theme.colors.white};
          max-width: 80%;
          min-width: 455px;
          max-height: 95%;
        }
      `}
    >
      <ModalContent>
        {isPlayButtonDisabled && (
          <MessageSection>
            <MessageContent>
              <span>
                {t(`matchView.pendingInvitation${LOCIZE_SUFFIX_KEY}`, {
                  defaultValue: 'Byli jste pozváni do hry.',
                })}{' '}
              </span>

              <BorderlessButton
                css={{ margin: 'initial', display: 'inline' }}
                labelT={{ key: 'closeWindow', defaultValue: 'Zavřít okno' }}
                onClick={handleClose}
              />
            </MessageContent>
          </MessageSection>
        )}
        <Heading id={modalHeadingId} css={{ paddingInline: 15 }}>
          {scene?.name}
        </Heading>
        <Flex css={{ marginTop: 15 }}>
          <div>
            <SceneContent
              image={scene?.image}
              selected={false}
              sceneCompleted={false}
              isModalView
              isEduLayout={IS_EDU}
            />
          </div>
          <div css={{ paddingLeft: 15, width: 450 }}>
            <div>
              {t(`matchView.tryOfficeGameWithGuide${LOCIZE_SUFFIX_KEY}`, {
                sceneName: tutorialSceneName || '',
                defaultValue:
                  'Nevíte si rady? Vyzkoušejte si hru s průvodcem v dějišti {{sceneName}}',
              })}
            </div>
            <div css={{ marginTop: 15 }}>
              <Button
                labelT={
                  IS_EASY_GAME
                    ? {
                        key: 'userBanner.menu.startTutorial',
                        defaultValue: 'Spustit hru s průvodcem',
                      }
                    : {
                        key: 'userBanner.menu.explainMediumGame',
                        defaultValue: 'Vysvětlit pokročilou hru',
                      }
                }
                disabled={IS_BUTTON_DISABLED}
                onClick={handleStartTutorial}
                variant="outline-primary"
              />
            </div>
          </div>
        </Flex>

        <Flex
          justifyContent="center"
          role="tablist"
          css={{ marginTop: 15, textAlign: 'center' }}
        >
          {gameMenuList.map((gameMenuItem) => (
            <BorderlessButton
              key={gameMenuItem.key}
              css={{ margin: 'unset', padding: 0 }}
              label={
                <NavigationLinkContent
                  isActive={selectedTabKey === gameMenuItem.key}
                >
                  {t('lobby.nav.' + gameMenuItem.key)}
                </NavigationLinkContent>
              }
              onClick={() => handleTabSelect(gameMenuItem.key)}
            />
          ))}
        </Flex>

        {gameMenuList.map((gameMenuItem) => (
          <SceneDetailModalTabPanel
            key={gameMenuItem.key}
            isHidden={selectedTabKey !== gameMenuItem.key}
            isPlayButtonDisabled={isPlayButtonDisabled}
            sceneId={scene?.id}
            gameDifficulty={gameDifficulty}
            gameType={gameMenuItem.gameType}
            opponentCount={opponentCount}
          >
            {gameMenuItem.key === 'training' && (
              <ScenesProgressHeading
                skipSimpleHeading
                gameType={gameType}
                gamesConfig={gamesConfig}
              />
            )}
            {gameMenuItem.key === 'tournament' && (
              <>
                {gameDifficulty === GameDifficulty.EASY && (
                  <div css={{ marginBlock: 8 }}>
                    <span>
                      {t(
                        `matchView.againstPlayerInAdvancedGame${LOCIZE_SUFFIX_KEY}`,
                        {
                          defaultValue:
                            'V pokročilé hře můžete hrát proti živým hráčům.',
                        },
                      )}
                    </span>
                  </div>
                )}
              </>
            )}
            {gameMenuItem.key === 'play' && (
              <>
                {isTraining && (
                  <div css={{ marginBlock: 8 }}>
                    {t('freeGameWarning.text')}
                  </div>
                )}
                {gameDifficulty === GameDifficulty.EASY && (
                  <div css={{ marginBlock: 8 }}>
                    <span>
                      {t(
                        `matchView.againstPlayerInAdvancedGame${LOCIZE_SUFFIX_KEY}`,
                        {
                          defaultValue:
                            'V pokročilé hře můžete hrát proti živým hráčům.',
                        },
                      )}{' '}
                    </span>
                    <span>
                      {t(`matchView.askToSomeoneToJoin${LOCIZE_SUFFIX_KEY}`, {
                        defaultValue:
                          'Zkuste se někoho zeptat, jestli se nepřípojí.',
                      })}
                    </span>
                  </div>
                )}
              </>
            )}
          </SceneDetailModalTabPanel>
        ))}
      </ModalContent>
    </Modal>
  );
};

const MessageSection = styled('div')({
  paddingInline: 20,
  marginBlock: 3,
});
const MessageContent = styled('div')({
  borderRadius: 4,
  padding: 10,
  textAlign: 'center',
  borderStyle: 'solid',
  borderWidth: 2,
  borderColor: colors.lobbyOrange,
});

const ModalContent = styled('div')({
  padding: 25,
});

export default SceneDetailModal;
